import { User } from 'lucide-react';
import { useCallback } from 'react';

import { CustomerSelect } from './CustomerSelect';

import { Button } from '@/components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthViewer } from '@/hooks/useAuthViewer';
import { cn } from '@/lib/utils';
import { RoleEnum } from '@/services/UserService';

interface CustomerSelectorProps {
  position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export function CustomerPopover({ position }: CustomerSelectorProps) {
  const { user, finalizeLogin, logout } = useAuth();

  const { authViewer } = useAuthViewer();

  const handleAuthViewer = useCallback(
    async (value: number) => {
      const { token } = await authViewer({ customerId: value });

      logout();

      await finalizeLogin(token);
    },
    [authViewer, finalizeLogin, logout],
  );

  if (user?.role.id !== RoleEnum.Root) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed z-50',
        position === 'top-right' && 'right-16 top-3',
        position === 'top-left' && 'left-3 top-3',
        position === 'bottom-right' && 'bottom-3 right-16',
        position === 'bottom-left' && 'bottom-3 left-3',
      )}
    >
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50"
          >
            <User className="h-5 w-5 text-gray-600" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-64 p-3" align="start">
          <div className="space-y-3">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium text-gray-900">Viewer Mode</p>
              <p className="text-xs text-gray-500">
                ({user?.customer ? 'Customer' : 'Root'})
              </p>
            </div>

            <CustomerSelect
              additionalOptions={[{ label: 'Root', value: '0' }]}
              defaultValue={String(user?.customer?.id ?? 0)}
              onValueChange={(value) => handleAuthViewer(Number(value))}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
